/**
 * TopicList container
 * Left side topic list; Common Question is on the top for home page followed by topics list
 * Users can access leftside topic list from article, topic, and home page
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { bindActionCreators } from 'redux';
import Glyph from 'kepler/lib/components/Glyph';
import { connect } from 'react-redux';
import i18n from '../../lib/reacti18n';
import Page from '../../lib/page';
import { trackLink } from '../../lib/analytics-helper';
import { getPageUrlInfoFromLocation } from '../../lib/location-helper';
import urlResource from '../constants/UrlResources';
import routeDidUpdate from '../actions/actions-router';
import { guideClicked } from '../actions/action-icf-page';

import getHistory from '../../lib/HistoryHelper';

const TopicList = (props) => {
  const {
    location, topics, isLoading, error, article, breadcrumb, blacklistedMarketICFFlag,
  } = props;
  const FPTIId = Page.getFPTIPageId();

  const popular = {
    common: i18n.message('smarthelp/home.home.common'),
    recommended: i18n.message('smarthelp/home.home.recommended'),
    home: i18n.message('smarthelp/home.home.home'),
    payPalGuide: i18n.message('smarthelp/home.home.payPalGuide'),
    payPalGuideDescription: i18n.message('smarthelp/home.home.guideDescription'),
  };
  const pg = getPageUrlInfoFromLocation(location);

  const popularClass = classNames({
    'topic-tree-elements': true,
    'topic-selected': (pg.resource.name === urlResource.HOME),
  });

  const parentTopic = (currentArticle, breadcrumbs) => {
    if (Array.isArray(breadcrumbs) && breadcrumbs[1]) {
      return breadcrumbs[1].id;
    } if (currentArticle && currentArticle.breadCrumbPath) {
      const topicTree = currentArticle.breadCrumbPath.split('/');
      return topicTree[1];
    }
    return null;
  };

  // FPTI tracking users' click behavior based on the triggered page
  const goToHome = () => {
    trackLink({ id: FPTIId, link: 'smarthelp-hcr-topic-list|common-questions' });
    getHistory().push('/home');
  };

  const goToTopicList = (e, topic) => {
    e.preventDefault();
    trackLink({ id: FPTIId, link: `smarthelp-hcr-topic-list|${topic.topicId}` });
    getHistory().push(`/topic/${topic.topicId}`);
  };

  const getTopicClass = (topic) => {
    if (pg.resource.name === urlResource.HOME || Page.isMobileLayout()) {
      return 'topic-tree-elements';
    } if (pg.resource.name === urlResource.TOPIC && String(topic.topicId) === pg.resource.id) {
      return 'topic-tree-elements topic-selected';
    } if (pg.resource.name === urlResource.ARTICLE && parentTopic(article, breadcrumb) === String(topic.topicId)) {
      return 'topic-tree-elements topic-selected';
    }
    return 'topic-tree-elements';
  };

  const getRecommendedPopularLabel = () => {
    if (Page.isLoggedIn()) {
      return popular.home;
    }
    return popular.common;
  };

  const getCommonQuestions = () => (
    <li className="topic-tree-elements-item topic-tree-elements-item__common-question">
      <button
        className={popularClass}
        onClick={() => goToHome()}
      >
        {getRecommendedPopularLabel()}
      </button>
    </li>
  );

  const payPalGuideClick = (e) => {
    e.preventDefault();
    const payPalGuide = urlResource.PAYPALGUIDE;
    trackLink({ id: 'homePage', link: `smarthelp-hcr-topic-list|${payPalGuide}` });
    props.guideClicked(false);
    props.routeDidUpdate({ pathname: `/${payPalGuide}` });
    getHistory().push(`/${payPalGuide}`);
  };

  const payPalHelpGuides = () => {
    // if remote config is disabled or not technical experience
    if (Page.isTechnicalExp() || blacklistedMarketICFFlag) {
      return null;
    }

    return (
      <li className="topic-tree-elements-item">
        <a // eslint-disable-line jsx-a11y/no-static-element-interactions
          className="topic-tree-elements"
          onClick={(e) => payPalGuideClick(e)}
          href={`/${Page.getCountry()}/smarthelp/${urlResource.PAYPALGUIDE}`}
        >
          <div className="paypal-guide-header">
            {popular.payPalGuide}
          </div>
          <Glyph icon={Page.getArrowDirectionStyle()} className="icon-arrow" aria-hidden="true" />
        </a>
      </li>
    );
  };

  const getTopicList = () => topics.map((topic) => (
    <li key={topic.topicId} className="topic-tree-elements-item">
      <a
        className={getTopicClass(topic)}
        key={topic.topicId}
        href={`/${Page.getCountry()}/smarthelp/topic/${topic.topicId}`}
        onClick={(e) => goToTopicList(e, topic)}
      >
        <div className="topic-tree-home-content">
          {topic.topicName}
        </div>
        <Glyph icon={Page.getArrowDirectionStyle()} className="icon-arrow" aria-hidden="true" />
      </a>

    </li>
  ));

  const renderTopicTree = () => {
    if (!isLoading && !error) {
      return (
        <nav>
          <ul className="topic-tree-container-home">
            {getCommonQuestions()}
            {payPalHelpGuides()}
            {getTopicList()}
          </ul>
        </nav>
      );
    }
    return null;
  };
  return renderTopicTree();
};

function mapStateToProps(state) {
  return {
    isLoading: state.topicList.isLoading,
    topics: state.topicList.topics,
    location: state.location,
    error: state.topicList.error,
    article: state.article.article,
    breadcrumb: state.breadcrumb.items,
    blacklistedMarketICFFlag: state.topicList.blacklistedMarketICFFlag,
  };
}
function mapDispatchToProps(dispatch) {
  return bindActionCreators({ routeDidUpdate, guideClicked }, dispatch);
}
TopicList.propTypes = {
  topics: PropTypes.arrayOf(PropTypes.shape({
    topicId: PropTypes.string,
    topicName: PropTypes.string,
  })),
  blacklistedMarketICFFlag: PropTypes.bool,
  isLoading: PropTypes.bool,
  error: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  routeDidUpdate: PropTypes.func.isRequired,
  guideClicked: PropTypes.func.isRequired,
};

TopicList.defaultProps = {
  topics: [],
  display: false,
  location: undefined,
};
export default connect(mapStateToProps, mapDispatchToProps)(TopicList);
